// Master-key vault: zero-knowledge key management.
//
// ONE random Master Key (MK) seals the user's secret bundle (PQC private keys +
// the random mail credential). MK is then wrapped INDEPENDENTLY by each unlocker
// (password, recovery code, optional passphrase). Any unlocker opens MK -> bundle.
// Changing / adding / removing an unlocker re-wraps only ITS copy of MK, so the
// bundle (and thus every previously encrypted mail) is never re-encrypted. The
// server stores ONLY ciphertext: it can never derive MK, the password, or the
// bundle.
//
// Primitives: PBKDF2-SHA256 (600k) -> AES-256-GCM, all via WebCrypto (browser +
// Node 18+). No third-party dependencies.

const PBKDF2_ITERATIONS = 600000;
const enc = new TextEncoder();
const dec = new TextDecoder();
const subtle = () => globalThis.crypto.subtle;

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const rand = (n) => globalThis.crypto.getRandomValues(new Uint8Array(n));

async function deriveWrapKey(secret, salt, iterations = PBKDF2_ITERATIONS) {
  const base = await subtle().importKey('raw', enc.encode(secret), 'PBKDF2', false, ['deriveKey']);
  return subtle().deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, base,
    { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

// Wrap raw MK bytes with a secret -> { salt, iv, ct } (base64). Throws on a wrong
// secret at unwrap (AES-GCM auth-tag mismatch).
async function wrapMK(mkRaw, secret) {
  const salt = rand(16), iv = rand(12);
  const key = await deriveWrapKey(secret, salt);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, key, mkRaw);
  return { kdf: 'PBKDF2-SHA256', iterations: PBKDF2_ITERATIONS, salt: b64(salt), iv: b64(iv), ct: b64(ct) };
}
async function unwrapMK(wrap, secret) {
  const key = await deriveWrapKey(secret, unb64(wrap.salt), wrap.iterations || PBKDF2_ITERATIONS);
  return new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: unb64(wrap.iv) }, key, unb64(wrap.ct)));
}

async function mkKey(mkRaw) { return subtle().importKey('raw', mkRaw, 'AES-GCM', false, ['encrypt', 'decrypt']); }
async function sealBundle(mkRaw, bundle) {
  const iv = rand(12); const key = await mkKey(mkRaw);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(bundle)));
  return { iv: b64(iv), ct: b64(ct) };
}
async function openBundle(blob, mkRaw) {
  const key = await mkKey(mkRaw);
  const pt = await subtle().decrypt({ name: 'AES-GCM', iv: unb64(blob.iv) }, key, unb64(blob.ct));
  return JSON.parse(dec.decode(pt));
}

// Recovery code: ~128-bit, grouped Crockford-ish base32 (Apple Recovery Key style).
// High entropy, no wordlist needed; case/format normalized on entry.
const B32 = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // no ambiguous I/L/O/0/1
export function newRecoveryCode() {
  const bytes = rand(26);
  let s = '';
  for (let i = 0; i < 26; i++) s += B32[bytes[i] % B32.length];
  return s.match(/.{1,5}/g).join('-');
}
export const normalizeCode = (c) => (c || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const norm = (which, secret) => (which === 'recovery' ? normalizeCode(secret) : secret);

// Login key: a password-derived value in a SEPARATE domain from the MK wrap, so
// the control plane can authenticate the user without ever seeing the password,
// and seeing the login key never reveals MK. Server stores only sha256(loginKey).
export async function deriveLoginKey(password, salt) {
  const base = await subtle().importKey('raw', enc.encode('kmlogin ' + password), 'PBKDF2', false, ['deriveBits']);
  const bits = await subtle().deriveBits({ name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' }, base, 256);
  return b64(bits);
}
export async function loginVerifier(loginKeyB64) {
  return b64(await subtle().digest('SHA-256', unb64(loginKeyB64)));
}

// ---------------------------------------------------------------------------
// High-level vault API. A `vault` is a pure-JSON, server-storable (ciphertext)
// object: { v, keys, unlockers:{ password?, recovery?, passphrase? }, login }.
// ---------------------------------------------------------------------------

export async function createVault(bundle, { password, recoveryCode, passphrase } = {}) {
  const mkRaw = rand(32);
  const vault = { v: 1, keys: await sealBundle(mkRaw, bundle), unlockers: {} };
  if (password) vault.unlockers.password = await wrapMK(mkRaw, password);
  if (recoveryCode) vault.unlockers.recovery = await wrapMK(mkRaw, normalizeCode(recoveryCode));
  if (passphrase) vault.unlockers.passphrase = await wrapMK(mkRaw, passphrase);
  if (password) {
    const salt = rand(16);
    vault.login = { salt: b64(salt), verifier: await loginVerifier(await deriveLoginKey(password, salt)) };
  }
  // A separate login verifier keyed on the recovery code, so a user who FORGOT
  // their password can still authenticate (to fetch the vault) and recover.
  if (recoveryCode) {
    const rsalt = rand(16);
    vault.recoveryLogin = { salt: b64(rsalt), verifier: await loginVerifier(await deriveLoginKey(normalizeCode(recoveryCode), rsalt)) };
  }
  return vault;
}

// Open the bundle with whichever secret the user supplies. `which` in
// {password, recovery, passphrase}. Returns { bundle, mkRaw } (mkRaw lets the
// caller re-wrap other unlockers without re-deriving MK). Throws on wrong secret.
export async function openVault(vault, which, secret) {
  const w = vault.unlockers?.[which];
  if (!w) throw new Error(`no ${which} unlocker on this vault`);
  const mkRaw = await unwrapMK(w, norm(which, secret));
  return { bundle: await openBundle(vault.keys, mkRaw), mkRaw };
}

// Add/replace an unlocker (re-wrap its copy of MK). MK and bundle untouched, so
// all previously encrypted mail stays decryptable. Needs mkRaw (from openVault).
export async function setUnlocker(vault, mkRaw, which, secret) {
  vault.unlockers[which] = await wrapMK(mkRaw, norm(which, secret));
  return vault;
}
export function removeUnlocker(vault, which) { delete vault.unlockers?.[which]; return vault; }

// Replace the recovery code: re-wrap MK + recompute the recovery-login verifier.
export async function setRecoveryCode(vault, mkRaw, recoveryCode) {
  const code = normalizeCode(recoveryCode);
  vault.unlockers.recovery = await wrapMK(mkRaw, code);
  const rsalt = rand(16);
  vault.recoveryLogin = { salt: b64(rsalt), verifier: await loginVerifier(await deriveLoginKey(code, rsalt)) };
  return vault;
}

// Change password: re-wrap the password copy + refresh the login verifier. MK
// unchanged -> history safe. Change passphrase = setUnlocker(..,'passphrase',..).
export async function changePassword(vault, mkRaw, newPassword) {
  await setUnlocker(vault, mkRaw, 'password', newPassword);
  const salt = rand(16);
  vault.login = { salt: b64(salt), verifier: await loginVerifier(await deriveLoginKey(newPassword, salt)) };
  return vault;
}
